import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CalculatorInput, CalculatorResult, CalculatorResultsGrid } from '@/components/calculators'
import {
  calculateMonthlyPayment,
  calculateDSCR,
  calculateMaxLoanForDSCR,
  formatCurrency,
  formatPercent,
} from '@/lib/calculators/formulas'

export default function DscrCalculator() {
  const [monthlyRent, setMonthlyRent] = useState(2400)
  const [vacancyRate, setVacancyRate] = useState(8)
  const [monthlyExpenses, setMonthlyExpenses] = useState(400)
  const [loanAmount, setLoanAmount] = useState(150000)
  const [interestRate, setInterestRate] = useState(7)
  const [loanTerm, setLoanTerm] = useState(30)
  const [targetDSCR, setTargetDSCR] = useState(1.25)

  const effectiveMonthlyRent = monthlyRent * (1 - vacancyRate / 100)
  const monthlyNOI = effectiveMonthlyRent - monthlyExpenses
  const annualNOI = monthlyNOI * 12
  const monthlyDebtService = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)
  const annualDebtService = monthlyDebtService * 12
  const dscr = calculateDSCR(monthlyNOI, monthlyDebtService)
  const maxLoan = calculateMaxLoanForDSCR(monthlyNOI, targetDSCR, interestRate, loanTerm)
  const monthlyCashFlow = monthlyNOI - monthlyDebtService

  const dscrStatus = dscr >= 1.25 ? 'Strong' : dscr >= 1.0 ? 'Marginal' : 'Below breakeven'

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 py-12">
      <Link to="/resources/calculators" className="prose-link">
        &larr; Back to Calculators
      </Link>
      <h1 className="text-fluid-h2 text-navy">DSCR Calculator</h1>

      <Card>
        <CardContent className="p-6">
          <p>
            Debt Service Coverage Ratio measures whether a property's net operating income covers its debt payments.
            Lenders typically require 1.20-1.25x for investment property loans. Below 1.0 means the property doesn't
            generate enough income to cover debt.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Property Income</h2>

            <CalculatorInput label="Monthly Gross Rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" />

            <CalculatorInput label="Vacancy Rate" value={vacancyRate} onChange={setVacancyRate} suffix="%" />

            <CalculatorInput
              label="Monthly Operating Expenses"
              value={monthlyExpenses}
              onChange={setMonthlyExpenses}
              prefix="$"
              tooltip="Taxes, insurance, maintenance, management"
            />

            <div className="text-muted-foreground text-sm">
              Monthly NOI: {formatCurrency(monthlyNOI)} | Annual NOI: {formatCurrency(annualNOI)}
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Loan Details</h2>

            <CalculatorInput label="Loan Amount" value={loanAmount} onChange={setLoanAmount} prefix="$" />

            <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} suffix="%" />

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Loan Term</span>
              <div className="flex gap-2" role="group" aria-label="Loan Term">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setLoanTerm(term)}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      loanTerm === term ? 'bg-navy text-white' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Max Loan Sizing</h2>

            <CalculatorInput
              label="Target DSCR"
              value={targetDSCR}
              onChange={setTargetDSCR}
              tooltip="Most lenders require 1.20-1.25x"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">DSCR Analysis</h2>

            <CalculatorResult label="DSCR" value={`${dscr.toFixed(2)}x`} highlight subtext={dscrStatus} />

            <CalculatorResultsGrid>
              <CalculatorResult label="Annual NOI" value={formatCurrency(annualNOI)} />
              <CalculatorResult label="Annual Debt Service" value={formatCurrency(annualDebtService)} />
              <CalculatorResult label="Monthly Cash Flow" value={formatCurrency(monthlyCashFlow)} />
              <CalculatorResult label="Monthly Debt Service" value={formatCurrency(monthlyDebtService)} />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Max Loan at {targetDSCR}x DSCR</h2>

            <CalculatorResult
              label={`Maximum Loan Amount`}
              value={formatCurrency(maxLoan)}
              subtext={`At ${formatPercent(interestRate, 1)} over ${loanTerm} years with ${targetDSCR}x DSCR`}
            />

            <div className="bg-muted/50 text-muted-foreground space-y-1 rounded-lg p-3 text-sm">
              <p>
                <strong>DSCR Benchmarks:</strong>
              </p>
              <p>1.25x+ = Strong (most lenders comfortable)</p>
              <p>1.10-1.24x = Marginal (may require reserves)</p>
              <p>1.00x = Breakeven (income just covers debt)</p>
              <p>&lt;1.00x = Negative (property loses money)</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
